import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";

/**
 * Rich loading state shown while a recommendation request is in flight.
 * - 6 book-shaped skeleton cards with a shimmer sweep.
 * - Rotating progress messages every 2.2s cycling through the real
 *   pipeline stages (LLM → covers → ratings → existence check) so the
 *   wait feels like progress instead of a static spinner.
 * - Pulsing eyebrow text + animated dots so the page doesn't appear
 *   frozen on first paint.
 */
export default function SkeletonResults() {
    const { t } = useTranslation();
    const [stage, setStage] = useState(0);
    const stages = [
        t("home.skeleton.thinking"),
        t("home.skeleton.searching"),
        t("home.skeleton.ratings"),
        t("home.skeleton.covers"),
        t("home.skeleton.verifying"),
        t("home.skeleton.almost"),
    ];

    useEffect(() => {
        const id = setInterval(() => setStage((s) => (s + 1) % stages.length), 2200);
        return () => clearInterval(id);
    }, [stages.length]);

    return (
        <section
            data-testid="skeleton-results"
            className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pb-24 md:pb-32"
        >
            <div className="border-t border-[var(--border)] pt-16 mb-12">
                <div className="flex items-center gap-2 text-[var(--accent)]">
                    <Sparkles size={14} className="animate-pulse" strokeWidth={1.5} />
                    <p className="text-xs uppercase tracking-[0.3em] font-medium">
                        {stages[stage]}
                        <span className="inline-block w-6 text-left">
                            <DotsAnim />
                        </span>
                    </p>
                </div>
                <h2
                    className="font-display text-4xl md:text-5xl text-[var(--ink-muted)] italic mt-3 mb-6"
                    style={{ fontVariationSettings: '"wght" 500, "wdth" 90, "opsz" 96, "slnt" -10' }}
                >
                    {t("home.skeleton.headline")}
                </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                    <SkeletonCard key={i} delay={i * 120} />
                ))}
            </div>
        </section>
    );
}

function SkeletonCard({ delay }) {
    return (
        <div
            data-testid="skeleton-card"
            className="border border-[var(--border)] bg-[var(--bg-elevated)] rounded-md p-5 overflow-hidden relative"
            style={{
                animation: `fade-in 0.5s ease-out ${delay}ms both`,
            }}
        >
            <div className="shimmer-base aspect-[2/3] w-32 rounded mb-4" />
            <div className="shimmer-base h-5 w-4/5 rounded mb-2" />
            <div className="shimmer-base h-3 w-3/5 rounded mb-4" />
            <div className="shimmer-base h-3 w-full rounded mb-1.5" />
            <div className="shimmer-base h-3 w-11/12 rounded mb-1.5" />
            <div className="shimmer-base h-3 w-9/12 rounded" />
            <div className="flex gap-2 mt-5">
                <div className="shimmer-base h-7 w-24 rounded-full" />
                <div className="shimmer-base h-7 w-24 rounded-full" />
            </div>
        </div>
    );
}

function DotsAnim() {
    const [n, setN] = useState(1);
    useEffect(() => {
        const id = setInterval(() => setN((x) => (x % 3) + 1), 380);
        return () => clearInterval(id);
    }, []);
    return ".".repeat(n);
}
